import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useFetch } from '../useFetch'
import { useAuth } from '../../contexts/AuthContext'

import { CONFIG } from '../../config'
import { BlockModel } from '../../types/court/court'
import { ApiError } from '../../types/exception/errorStatus'
import { getErrorMessage } from '../../utils/exception/exceptionUtils'
import { formatDateTimesInText } from '../../utils/dateUtils'

const buildQueryParams = (from: string, to: string) =>
  new URLSearchParams({
    from,
    to,
  }).toString()

export function useBlocks() {
  const { t, i18n } = useTranslation('exceptions')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const pendingRequestsRef = useRef(0)
  const { authInfo } = useAuth()
  const { fetcher } = useFetch(authInfo?.accessToken, authInfo?.refreshToken)

  const resetState = () => {
    setLoading(false)
    setError(null)
  }

  const blocks = async (
    courtId: number,
    from: string,
    to: string
  ): Promise<BlockModel[]> => {
    if (pendingRequestsRef.current === 0) {
      setError(null)
    }

    pendingRequestsRef.current += 1
    setLoading(true)

    try {
      const queryParams = buildQueryParams(from, to)
      const data = await fetcher<BlockModel[]>(
        `${CONFIG.BOUNCE_API}/api/v1/courts/${courtId}/blocks?${queryParams}`
      )

      if (!data) {
        return []
      }

      return data.map(block => ({
        ...block,
        courtId,
      }))
    } catch (caughtError) {
      console.log(caughtError)

      const message =
        caughtError instanceof ApiError
          ? formatDateTimesInText(
              getErrorMessage(caughtError.data),
              i18n.language
            )
          : t('exceptions:unknown')

      setError(currentError => currentError ?? message)

      return []
    } finally {
      pendingRequestsRef.current = Math.max(
        0,
        pendingRequestsRef.current - 1
      )

      if (pendingRequestsRef.current === 0) {
        setLoading(false)
      }
    }
  }

  return {
    blocks,
    loading,
    error,
    resetState,
  }
}
